import { type FC, useMemo, useCallback, useState, type ReactNode } from 'react';

import { useScrollTrigger } from '@mui/material';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Price } from '@vakantiesnl/components/src/atoms/Price/Price';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { isOfferWithZooverDeal } from '@vakantiesnl/components/src/utils/accommodation/zooverDealHelpers';
import {
	AccommodationSections,
	type AccommodationSectionType,
	MENU_OFFSET,
	MENU_SCROLL_DURATION,
} from '@vakantiesnl/components/src/utils/accoSections';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { usePartyComposition } from '@vakantiesnl/services/src/stores/globalFiltersStore';
import { isPackageOffer } from '@vakantiesnl/services/src/util';
import { buildShortSummarizedPartyString } from '@vakantiesnl/services/src/util/partyUtils';
import { type MicroCopy, type Search } from '@vakantiesnl/types';
import dayjs from 'dayjs';
import { isUndefined } from 'lodash';
import dynamic from 'next/dynamic';
import { Link as ReactScrollLink } from 'react-scroll';

import { useStyles } from './AccommodationStickyOffer.style';
import { isZoover } from '../../utils';

const Slide = dynamic(() => import('@mui/material/Slide').then((module) => ({ default: module.default })), {
	ssr: false,
});

type AccommodationStickyOfferProps = {
	children?: ReactNode;
	/** The selected offer for the accommodation */
	selectedOffer?: Search.OfferItem | Search.AccommodationOnlyOffer;
	/** Microcopies passed as prop in order to display on Storybook */
	microCopies: MicroCopy;
	/** Make the drawer open/closed */
	drawerOpened?: boolean;
	/** Callback to trigger on click */
	onClick?: () => void;
	/** Accommodation data */
	accommodation: Search.FullAccommodationItem;
	/** Number of adlinks needed for tracking event */
	adlinksCount?: number;
	/** Whether the price of the pricecard is in the viewport */
	isPriceInView?: boolean;
	/** Whether the offers are loading */
	isLoadingOffers?: boolean;
};

export const nonBookableAccommodationMenuItems: AccommodationSectionType[] = [
	AccommodationSections.Pictures,
	AccommodationSections.Facilities,
	AccommodationSections.Reviews,
];

export const bookableAccommodationMenuItems: AccommodationSectionType[] =
	process.env.NEXT_PUBLIC_BRAND === 'ZVRNL'
		? [
				AccommodationSections.Pictures,
				AccommodationSections.Availability,
				AccommodationSections.Facilities,
				AccommodationSections.Reviews,
			]
		: [
				AccommodationSections.Pictures,
				AccommodationSections.Overview,
				AccommodationSections.Availability,
				AccommodationSections.Facilities,
			];

export const AccommodationStickyOffer: FC<AccommodationStickyOfferProps> = ({
	selectedOffer,
	microCopies,
	drawerOpened = false,
	onClick,
	accommodation,
	adlinksCount,
	isPriceInView,
	isLoadingOffers,
}) => {
	const { classes } = useStyles({ isOpen: drawerOpened });
	const party = usePartyComposition();
	const { isDesktop, isDesktopXl } = useBreakpoints();
	const [activeItem, setActiveItem] = useState<AccommodationSectionType>();

	const isBookableAccommodation = useMemo(
		() => accommodation.isBookableQenner || accommodation.isBookable,
		[accommodation.isBookable, accommodation.isBookableQenner],
	);

	const trigger = useScrollTrigger({
		threshold: 72,
		disableHysteresis: true,
	});

	const isZooverDeal = useMemo(() => isOfferWithZooverDeal(selectedOffer), [selectedOffer]);

	/** Builds the offer info above the price (date and PAX) */
	const pricePrefix: string | undefined = useMemo(() => {
		if (selectedOffer) {
			const dateString = dayjs(selectedOffer.departureDateYYYYMMDD, 'YYYY-MM-DD').format('DD-MM-YYYY');
			const partyString = buildShortSummarizedPartyString(party, microCopies);

			return `${dateString} | ${partyString}`;
		}
	}, [selectedOffer, microCopies, party]);

	const menuItems = useMemo(() => {
		if (isBookableAccommodation) {
			return bookableAccommodationMenuItems;
		} else {
			return nonBookableAccommodationMenuItems;
		}
	}, [isBookableAccommodation]);

	const handleOnclick = useCallback(() => {
		onClick && onClick();
	}, [onClick]);

	const handleSetActiveButton = useCallback((item: AccommodationSectionType) => {
		setActiveItem(item);
	}, []);

	const onHeaderLinkClick = useCallback(
		(menuItem: string) => () => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) => {
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ onPageNavBarClick }) =>
					track(
						onPageNavBarClick(
							accommodation,
							menuItem,
							microCopies[`accommodation.menuList.${menuItem}`],
							undefined,
							adlinksCount,
						),
					),
				);
			});
		},
		[accommodation, microCopies, adlinksCount],
	);

	return (
		<>
			{isDesktop && trigger && (
				<Slide
					appear={false}
					direction="down"
					in={trigger}
					unmountOnExit={false}
					data-cy="accommodation-sticky-offer"
				>
					<div className={classes.priceDrawerBar}>
						<div className={classes.priceDrawerBarContent}>
							<Typography variant="headingSm" as="h5" className={classes.accommodationTitle}>
								{accommodation.title}
							</Typography>
							<div className={classes.buttonContainer}>
								{!drawerOpened &&
									menuItems.map((menuItem, index) => (
										<ReactScrollLink
											to={menuItem}
											offset={MENU_OFFSET}
											smooth={true}
											duration={MENU_SCROLL_DURATION}
											onSetActive={handleSetActiveButton}
											spy={true}
											key={`${menuItem}${index}`}
										>
											<Button
												variant="tertiary"
												active={activeItem === menuItem}
												onClick={onHeaderLinkClick(menuItem)}
											>
												{microCopies[`accommodation.menuList.${menuItem}`]}
											</Button>
										</ReactScrollLink>
									))}
							</div>

							{trigger && !isPriceInView && !isLoadingOffers && (
								<div className={classes.priceToggle}>
									{!isUndefined(selectedOffer) && (
										<div className={classes.priceContainer} onClick={handleOnclick}>
											<Price
												price={selectedOffer?.priceTotal}
												prefix={pricePrefix || microCopies['common.perPersonFrom']}
												priceWithoutDiscount={
													isPackageOffer(selectedOffer)
														? selectedOffer.originalPricePerAdult
														: undefined
												}
												className={classes.price}
												rounded={false}
												primaryColor={false}
												showDiscountTag={isZooverDeal}
											/>
										</div>
									)}
									{!!onClick && (
										<Button
											variant="primary"
											size="small"
											onClick={handleOnclick}
											data-cy="drawer-CTA-button"
										>
											{isZoover && isDesktopXl
												? microCopies['common.bookNowBrand']
												: microCopies['common.bookNow']}
										</Button>
									)}
								</div>
							)}
						</div>
					</div>
				</Slide>
			)}
			{!isDesktop && trigger && isBookableAccommodation && (
				<Slide
					onClick={undefined}
					appear={false}
					direction="up"
					in={trigger && !isPriceInView}
					unmountOnExit={false}
					data-cy="accommodation-sticky-offer"
				>
					<div className={classes.priceDrawerBar}>
						<div className={classes.priceDrawerBarContent}>
							{!isUndefined(selectedOffer) && (
								<div className={classes.priceContainer}>
									<Price
										price={selectedOffer?.priceTotal}
										prefix={pricePrefix || microCopies['common.perPersonFrom']}
										priceWithoutDiscount={
											isPackageOffer(selectedOffer)
												? selectedOffer.originalPricePerAdult
												: undefined
										}
										className={classes.price}
										rounded={false}
										primaryColor={false}
										showDiscountTag={isZooverDeal}
									/>
								</div>
							)}

							<Button variant="primary" size="small" onClick={handleOnclick} data-cy="drawer-CTA-button">
								{isZoover ? microCopies['common.bookNowBrand'] : microCopies['common.bookNow']}
							</Button>
						</div>
					</div>
				</Slide>
			)}
		</>
	);
};
